import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import DaterangePicker from "../../common/Daterangepicker";

// ==========================================
// 1. SHARED CONSTANTS & UTILITIES (Task #25)
// ==========================================
const SEVERITY_THRESHOLDS = {
  CRITICAL: 5000,
  HIGH: 2000,
  MEDIUM: 500,
};

const getSeverityLevel = (amount) => {
  if (amount >= SEVERITY_THRESHOLDS.CRITICAL) return "Critical";
  if (amount >= SEVERITY_THRESHOLDS.HIGH) return "High";
  if (amount >= SEVERITY_THRESHOLDS.MEDIUM) return "Medium";
  return "Low";
};

// Guarded against division-by-zero, NaN, and Infinity (Task #3)
const formatCurrency = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    Number.isNaN(amount) ||
    !Number.isFinite(amount)
  )
    return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyShort = (amount) => {
  if (
    amount === undefined ||
    amount === null ||
    Number.isNaN(amount) ||
    !Number.isFinite(amount)
  )
    return "₹0";
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}k`;
  return formatCurrency(amount);
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

// Uses named CSS custom properties for central mapping (Task #23)
const getRecoverySeverity = (amount) => {
  if (amount >= SEVERITY_THRESHOLDS.CRITICAL) {
    return {
      label: "Critical",
      styleName: "var(--color-severity-critical)",
      className: "bg-red-100 text-red-700 border border-red-200",
      icon: "ti-alert-triangle",
    };
  }
  if (amount >= SEVERITY_THRESHOLDS.HIGH) {
    return {
      label: "High",
      styleName: "var(--color-severity-high)",
      className: "bg-orange-100 text-orange-700 border border-orange-200",
      icon: "ti-alert-circle",
    };
  }
  if (amount >= SEVERITY_THRESHOLDS.MEDIUM) {
    return {
      label: "Medium",
      styleName: "var(--color-severity-medium)",
      className: "bg-amber-100 text-amber-700 border border-amber-200",
      icon: "ti-info-circle",
    };
  }
  return {
    label: "Low",
    styleName: "var(--color-severity-low)",
    className: "bg-slate-100 text-slate-700 border border-slate-200",
    icon: "ti-check",
  };
};

// Severity Info Tooltip Component - With Label
const SeverityInfoTooltip = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isVisible = isOpen || isHovered;

  return (
    <div className="relative inline-flex items-center gap-1 ml-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          inline-flex items-center justify-center 
          w-6 h-6 rounded-full 
          transition-all duration-200
          ${
            isVisible
              ? "bg-red-500 text-white ring-2 ring-red-400 ring-offset-2 scale-110"
              : "bg-gray-200 text-gray-600 hover:bg-red-100 hover:text-red-600 hover:ring-2 hover:ring-red-200"
          }
          focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2
        `}
        aria-label="Severity classification information"
        aria-expanded={isVisible}
        type="button"
      >
        <i className="ti ti-info-circle text-base" />
      </button>

      {isVisible && (
        <div
          className="absolute z-[9999] bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-slate-800 text-white rounded-lg shadow-2xl p-4 text-xs pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200 border border-slate-700"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-bold text-white/90">
              Severity Classification Logic
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors p-0.5"
              aria-label="Close tooltip"
              type="button"
            >
              <i className="ti ti-x text-sm" />
            </button>
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between items-center border-b border-white/10 pb-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                Critical
              </span>
              <span className="font-mono text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">
                ≥ ₹5,000
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-white/10 pb-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                High
              </span>
              <span className="font-mono text-orange-300 bg-orange-500/10 px-1.5 py-0.5 rounded">
                ₹2,000 – ₹4,999
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-white/10 pb-1">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                Medium
              </span>
              <span className="font-mono text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">
                ₹500 – ₹1,999
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" />
                Low
              </span>
              <span className="font-mono text-slate-300 bg-slate-500/10 px-1.5 py-0.5 rounded">
                &lt; ₹500
              </span>
            </div>
          </div>

          <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-gray-300 flex items-center gap-1">
            <i className="ti ti-info-circle text-[10px]" />
            Based on recovery amount thresholds
          </div>

          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-b border-slate-700" />
        </div>
      )}
    </div>
  );
};

// ==========================================
// 2. FILTER & MANAGEMENT BAR SUB-COMPONENT
// ==========================================
const FiltersBar = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  sourceFilter,
  setSourceFilter,
  sortBy,
  setSortBy,
  uniqueStatuses,
  uniqueSources,
  handleExportCSV,
  disableExport,
}) => {
  const hasActiveFilters =
    statusFilter !== "all" ||
    sourceFilter !== "all" ||
    searchTerm.trim() !== "";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm space-y-4">
      {/* Search on its own full-width row (Task #19) */}
      <div className="w-full">
        <div className="relative">
          {/* Visually-hidden label for proper screen-reader compliance (Task #9) */}
          <label htmlFor="search-bookings" className="sr-only">
            Search by booking code
          </label>
          <i className="ti ti-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
          <input
            id="search-bookings"
            type="text"
            placeholder="Search by booking code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
          />
        </div>
      </div>

      {/* Dropdowns + Export layout underneath (Task #19) */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
            aria-label="Filter by booking status"
          >
            <option value="all">All statuses</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
            aria-label="Filter by source"
          >
            <option value="all">All sources</option>
            {uniqueSources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
            aria-label="Sort recovery cases"
          >
            <option value="highest-recovery">Highest recovery</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>

        <button
          onClick={handleExportCSV}
          disabled={disableExport}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
            disableExport
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-red-50 text-red-700 hover:bg-red-100"
          }`}
        >
          <i className="ti ti-download text-sm" />
          Export CSV
        </button>
      </div>

      {/* Active filter chips individually removable with an "×" (Task #19) */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-xs text-gray-400 mr-1">Active filters:</span>
          {statusFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Status: {statusFilter}
              <button
                onClick={() => setStatusFilter("all")}
                className="ml-1 text-slate-400 hover:text-red-600 focus:outline-none font-bold"
                aria-label="Remove status filter"
              >
                ×
              </button>
            </span>
          )}
          {sourceFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Source: {sourceFilter}
              <button
                onClick={() => setSourceFilter("all")}
                className="ml-1 text-slate-400 hover:text-red-600 focus:outline-none font-bold"
                aria-label="Remove source filter"
              >
                ×
              </button>
            </span>
          )}
          {searchTerm.trim() !== "" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Search: "{searchTerm}"
              <button
                onClick={() => setSearchTerm("")}
                className="ml-1 text-slate-400 hover:text-red-600 focus:outline-none font-bold"
                aria-label="Remove search term"
              >
                ×
              </button>
            </span>
          )}
          <button
            onClick={() => {
              setStatusFilter("all");
              setSourceFilter("all");
              setSearchTerm("");
            }}
            className="text-xs text-red-500 hover:text-red-600 font-medium ml-auto"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 3. STATISTICS HERO CARDS SUB-COMPONENT
// ==========================================
const StatisticsCards = ({ stats, monthlyTrend, criticalCount }) => {
  // MoM indicator logic using monthlyTrend data (Task #12) with zero validation (Task #3)
  const momTrendPercentage = useMemo(() => {
    if (monthlyTrend.length < 2) return { value: 0, isPositive: true };
    const latest = monthlyTrend[monthlyTrend.length - 1]?.amount || 0;
    const previous = monthlyTrend[monthlyTrend.length - 2]?.amount || 0;

    if (previous === 0) {
      return { value: latest > 0 ? 100 : 0, isPositive: latest >= 0 };
    }
    const diff = ((latest - previous) / previous) * 100;
    return { value: Math.abs(diff).toFixed(0), isPositive: diff >= 0 };
  }, [monthlyTrend]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-8">
      {/* Main Stat Card */}
      <div className="md:col-span-5 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <p className="text-sm opacity-80 flex items-center gap-2">
          <i className="ti ti-currency-rupee" /> Total pending recovery
        </p>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-4xl font-semibold mt-2 tracking-tight">
            {formatCurrency(stats.totalPendingAmount)}
          </h2>
          {monthlyTrend.length >= 2 && (
            <span
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white"
              title="Month over Month Trend Indicator"
            >
              {momTrendPercentage.isPositive ? "↑" : "↓"}{" "}
              {momTrendPercentage.value}% MoM
            </span>
          )}
        </div>
        <div className="mt-5 flex gap-6">
          <div>
            <p className="text-xs opacity-70">Active cases</p>
            <p className="text-xl font-medium">{stats.caseCount}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Average recovery</p>
            <p className="text-xl font-medium">
              {formatCurrency(stats.averagePending)}
            </p>
          </div>
          <div>
            <p className="text-xs opacity-70">Collection rate</p>
            <p className="text-xl font-medium">
              {stats.collectionRate.toFixed(0)}%
            </p>
          </div>
        </div>
      </div>

      {/* 3 Secondary Cards with left-border accents tying them to the theme (Task #13) */}
      <div className="md:col-span-7 grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-blue-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-blue-50 text-blue-600 rounded-md">
              <i className="ti ti-chart-bar" />
            </span>
            <span className="font-medium">Total charges</span>
          </div>
          <p className="text-2xl font-semibold text-slate-800 mt-2">
            {formatCurrencyShort(stats.totalCharges)}
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-amber-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-amber-50 text-amber-600 rounded-md">
              <i className="ti ti-building" />
            </span>
            <span className="font-medium">Security locked</span>
          </div>
          <p className="text-2xl font-semibold text-slate-800 mt-2">
            {formatCurrencyShort(stats.totalSecurityDeposit)}
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-red-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-red-50 text-red-600 rounded-md">
              <i className="ti ti-trending-up" />
            </span>
            <span className="font-medium">Critical cases</span>
          </div>
          <p className="text-2xl font-semibold text-red-600 mt-2">
            {criticalCount}
          </p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 4. ANALYTICS & VISUAL CHARTS ROW SUB-COMPONENT
// ==========================================
const ChartsRow = ({
  severityDistribution,
  sourceDistribution,
  monthlyTrend,
  selectedSeverity,
  onSeverityClick,
}) => {
  const [activeTab, setActiveTab] = useState("source");
  const [hoveredSource, setHoveredSource] = useState(null);

  const totalAmount = sourceDistribution.reduce((sum, s) => sum + s.amount, 0);

  const conicGradientStops = useMemo(() => {
    let cumulativePercent = 0;
    const stops = [];
    sourceDistribution.forEach((source) => {
      const percent = totalAmount > 0 ? (source.amount / totalAmount) * 100 : 0;
      stops.push(
        `${source.color} ${cumulativePercent}% ${cumulativePercent + percent}%`,
      );
      cumulativePercent += percent;
    });
    return stops.length > 0
      ? `conic-gradient(${stops.join(", ")})`
      : "lightgray";
  }, [sourceDistribution, totalAmount]);

  const SeverityDistributionView = () => {
    const maxAmount = Math.max(
      ...Object.values(severityDistribution).map((d) => d.amount),
      1,
    );
    return (
      <div className="space-y-3">
        {Object.entries(severityDistribution).map(([level, data]) => {
          const barWidth = (data.amount / maxAmount) * 100;
          const isSelected = selectedSeverity === level;
          return (
            <div
              key={level}
              className={`space-y-1 cursor-pointer p-1.5 rounded-lg transition-all ${
                isSelected
                  ? "bg-red-50 ring-1 ring-red-200"
                  : "hover:bg-slate-50"
              }`}
              onClick={() => onSeverityClick(isSelected ? null : level)}
            >
              <div className="flex justify-between text-xs">
                <span className="font-medium flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: data.color }}
                  ></span>
                  {level}{" "}
                  {isSelected && (
                    <span className="text-[10px] text-red-500 font-bold">
                      (Active Filter)
                    </span>
                  )}
                </span>
                <span className="text-gray-500">
                  {data.count} cases · {formatCurrencyShort(data.amount)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${barWidth}%`, backgroundColor: data.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const SourceDistributionView = () => {
    const maxAmount = Math.max(...sourceDistribution.map((s) => s.amount), 1);

    return (
      <div className="space-y-3">
        {sourceDistribution.slice(0, 6).map((source) => {
          const barWidth = (source.amount / maxAmount) * 100;
          const sourcePercent =
            totalAmount > 0
              ? ((source.amount / totalAmount) * 100).toFixed(1)
              : 0;

          return (
            <div
              key={source.name}
              className="relative group cursor-pointer p-1.5 rounded-lg hover:bg-slate-50 transition-all"
              onMouseEnter={() => setHoveredSource(source.name)}
              onMouseLeave={() => setHoveredSource(null)}
            >
              <div className="flex justify-between text-xs mb-0.5">
                <span className="font-medium flex items-center gap-2 text-slate-700">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: source.color }}
                  ></span>
                  <span className="truncate max-w-[120px]">{source.name}</span>
                  <span className="text-gray-400 font-normal text-[10px]">
                    ({source.count} cases)
                  </span>
                </span>
                <span className="font-semibold text-slate-700">
                  {formatCurrencyShort(source.amount)}
                </span>
              </div>

              <div className="h-6 bg-gray-100 rounded-md overflow-hidden relative">
                <div
                  className="h-full rounded-md transition-all duration-500 flex items-center px-2"
                  style={{
                    width: `${Math.max(barWidth, 4)}%`,
                    backgroundColor: source.color,
                    opacity: 0.85,
                  }}
                >
                  <span className="text-white text-[10px] font-bold">
                    {sourcePercent}%
                  </span>
                </div>

                {/* Tooltip on hover */}
                {hoveredSource === source.name && (
                  <div className="absolute z-10 -top-8 left-1/2 transform -translate-x-1/2 bg-slate-950 text-white text-[10px] px-2 py-1 rounded shadow-md pointer-events-none whitespace-nowrap border border-slate-800">
                    {formatCurrency(source.amount)} ({sourcePercent}% of total)
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Show "View All" if there are more sources */}
        {sourceDistribution.length > 6 && (
          <div className="text-center pt-2">
            <button
              className="text-[10px] text-red-500 hover:text-red-600 font-medium hover:underline"
              onClick={() => {
                /* Implement view all functionality */
              }}
            >
              + View all {sourceDistribution.length} sources
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
            <i className="ti ti-chart-pie text-red-500" /> Severity
            classification
            <SeverityInfoTooltip />
          </h3>
          {selectedSeverity && (
            <button
              onClick={() => onSeverityClick(null)}
              className="text-[11px] text-red-500 hover:underline"
            >
              Reset
            </button>
          )}
        </div>
        <SeverityDistributionView />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
            <i className="ti ti-layers" /> Channel allocation
          </h3>
          <div className="inline-flex rounded-md p-0.5 bg-slate-100 border border-slate-200">
            <button
              onClick={() => setActiveTab("severity")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeTab === "severity" ? "bg-white shadow-xs text-red-600 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
            >
              Severity
            </button>
            <button
              onClick={() => setActiveTab("source")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeTab === "source" ? "bg-white shadow-xs text-red-600 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
            >
              Sources
            </button>
          </div>
        </div>
        {activeTab === "severity" ? (
          <SeverityDistributionView />
        ) : (
          <SourceDistributionView />
        )}
      </div>
    </div>
  );
};

// ==========================================
// 5. OPERATIONAL PIPELINE FUNNEL SUB-COMPONENT
// ==========================================
const RecoveryFunnel = ({ funnelData }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-medium text-slate-800 flex items-center gap-2">
          <i className="ti ti-chart-arrows" /> Workflow funnel pipeline
          <span className="text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            Estimated Framework
          </span>
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-slate-700 rounded-full" /> Volume Base
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full" /> Exposure Cap
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full" /> Resolved
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {funnelData.map((stage, idx) => {
          let conversionAnnotation = null;
          if (idx > 0 && funnelData[idx - 1].count > 0) {
            const conversionRate = (
              (stage.count / funnelData[idx - 1].count) *
              100
            ).toFixed(0);
            conversionAnnotation = `${funnelData[idx - 1].stage} → ${stage.stage}: ${conversionRate}% rate`;
          }

          return (
            <div key={idx} className="space-y-1 relative">
              {conversionAnnotation && (
                <div className="text-[10px] font-bold text-gray-400 bg-slate-50 px-2 py-0.5 rounded border border-dashed border-slate-200 inline-block -mt-1 mb-1">
                  <i className="ti ti-arrow-forward mr-1 text-[9px]" />{" "}
                  {conversionAnnotation}
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 font-medium">{stage.stage}</span>
                <span className="font-semibold text-slate-700">
                  {stage.count} cases
                </span>
              </div>
              <div className="relative">
                <div
                  className="h-6 rounded-l-md transition-all duration-500 flex items-center px-2 shadow-xs"
                  style={{
                    width: `${Math.max(stage.percentage, 8)}%`,
                    backgroundColor: stage.color,
                    opacity: 0.85,
                  }}
                >
                  <span className="text-white text-[10px] font-bold">
                    {stage.percentage.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// 6. TOP CASES CONTEXT QUICK BUTTON WIDGET
// ==========================================
const TopCasesWidget = ({ topCases, onInitiateCall }) => {
  if (topCases.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-800 flex items-center gap-2">
          <i className="ti ti-crown text-amber-500" /> High liability exposure
          targets
        </h3>
        <span className="text-xs text-gray-400">
          Top {topCases.length} by amount
        </span>
      </div>
      <div className="space-y-2.5">
        {topCases.map((caseItem, idx) => {
          const severity = getRecoverySeverity(caseItem.recoveryAmount);
          return (
            <div
              key={caseItem.id}
              className="flex justify-between items-center p-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-mono font-bold text-slate-400 w-5">
                  #{idx + 1}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-slate-700 truncate">
                    {caseItem.bookingCode}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-gray-400 font-medium">
                      {caseItem.source}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="text-[11px] text-gray-400">
                      {caseItem.bookingStatus}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase ${severity.className}`}
                >
                  {severity.label}
                </span>
                <span className="font-bold text-red-600 text-sm">
                  {formatCurrency(caseItem.recoveryAmount)}
                </span>

                <button
                  onClick={() => onInitiateCall(caseItem.bookingCode)}
                  className="p-1.5 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-md transition-colors border border-slate-200"
                  title={`Call agent for case ${caseItem.bookingCode}`}
                  aria-label={`Call pipeline management agent assigned to booking parameter ${caseItem.bookingCode}`}
                >
                  <i className="ti ti-phone text-xs" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// 7. EXPANDABLE COMPLIANCE RECORDS DATA TABLE
// ==========================================
const RecoveryTable = ({
  bookings,
  expandedRowId,
  toggleRowExpansion,
  handleRowKeyDown,
  onStatusUpdate,
  onBookingClick,
}) => {
  return (
    <div className="divide-y divide-slate-100">
      {bookings.map((booking) => {
        const isExpanded = expandedRowId === booking.id;
        const severity = getRecoverySeverity(booking.recoveryAmount);

        const exceedPercent =
          booking.securityDeposit > 0
            ? (
                (booking.recoveryAmount / booking.securityDeposit) *
                100
              ).toFixed(0)
            : "0";

        const absoluteTotalCost = booking.totalChargesAmount || 0;
        const securityDepositCovered = Math.min(
          booking.securityDeposit || 0,
          absoluteTotalCost,
        );
        const coveredWidthPercent =
          absoluteTotalCost > 0
            ? (securityDepositCovered / absoluteTotalCost) * 100
            : 0;

        const handleRowClick = (e) => {
          if (e.target.closest("button") || e.target.closest("select")) {
            return;
          }
          // Navigate using query parameters like: viewallbookings?bookingid=BOOK123&display=true
          onBookingClick(booking);
        };

        return (
          <React.Fragment key={booking.id}>
            <div
              className={`px-5 py-4 hover:bg-slate-50 transition-all cursor-pointer outline-none focus:bg-slate-50 ${isExpanded ? "bg-slate-50 font-medium" : ""}`}
              // onClick={handleRowClick}
              onKeyDown={(e) => handleRowKeyDown(e, booking.id)}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} details for case code ${booking.bookingCode}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={
                        "font-bold text-slate-800 text-sm tracking-tight hover:text-red-600 transition-colors"
                      }
                      onClick={handleRowClick}
                    >
                      {booking.bookingCode}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${severity.className}`}
                    >
                      <i className={`ti ${severity.icon} mr-1`} />{" "}
                      {severity.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-1 font-normal">
                    <span>Hub: {booking.source || "N/A"}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>State: {booking.bookingStatus || "N/A"}</span>
                  </div>
                </div>

                <div className="hidden md:block flex-shrink-0 text-xs text-gray-400 font-normal">
                  <div>{formatDateTime(booking.bookFrom).split(",")[0]}</div>
                </div>

                <div className="flex-shrink-0 text-right">
                  <div className="inline-flex flex-col items-end">
                    <span className="text-lg font-bold text-red-600 tracking-tight">
                      {formatCurrency(booking.recoveryAmount)}
                    </span>
                    <span className="text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-sm mt-0.5">
                      +{exceedPercent}% over deposit
                    </span>
                  </div>
                </div>

                <div className="flex-shrink-0 text-gray-400">
                  <i
                    className={`ti ${isExpanded ? "ti-chevron-up" : "ti-chevron-down"} text-sm`}
                  />
                </div>
              </div>
            </div>

            {isExpanded && (
              <div className="px-5 py-5 bg-slate-50 border-t border-b border-slate-100 animate-in fade-in duration-200">
                <div className="mb-5 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <div className="flex justify-between text-xs font-semibold mb-1.5 text-slate-600">
                    <span>Security Deposit Coverage Spectrum</span>
                    <span className="text-emerald-600 font-bold">
                      {coveredWidthPercent.toFixed(0)}% Offset Covered
                    </span>
                  </div>
                  <div className="w-full h-3 bg-red-200 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${coveredWidthPercent}%` }}
                      title="Protected Threshold"
                    />
                    <div
                      className="h-full bg-red-500 flex-1"
                      title="Unsecured Risk Margin Gap"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-medium text-gray-400 mt-1">
                    <span>
                      Deposit Baseline:{" "}
                      {formatCurrency(booking.securityDeposit)}
                    </span>
                    <span className="text-red-500 font-bold">
                      Shortfall Liability:{" "}
                      {formatCurrency(booking.recoveryAmount)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-5">
                  <div className="col-span-12 md:col-span-7">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-receipt text-gray-400" /> Assessment
                        Line Items
                      </h4>
                      {!booking.chargesList ||
                      booking.chargesList.length === 0 ? (
                        <p className="text-gray-400 text-sm text-center py-4">
                          No individual line statements recorded
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          {booking.chargesList.map((charge, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-start py-2 border-b border-slate-100 last:border-0 text-xs"
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-700">
                                    {charge.typeText || "Charge Item"}
                                  </span>
                                  {charge.unitValue && (
                                    <span className="text-gray-400 bg-slate-100 px-1 rounded text-[10px]">
                                      {charge.unitValue} units
                                    </span>
                                  )}
                                </div>
                                {charge.comments && (
                                  <p className="text-gray-400 text-[11px] italic mt-0.5">
                                    "{charge.comments}"
                                  </p>
                                )}
                              </div>
                              <span className="font-bold text-red-500">
                                {formatCurrency(charge.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-xs font-bold">
                        <span className="text-slate-600">
                          Total Structural Claims
                        </span>
                        <span className="text-red-600 text-sm">
                          {formatCurrency(booking.totalChargesAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-5">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs h-full flex flex-col justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                          <i className="ti ti-chart-pie text-gray-400" />{" "}
                          Booking Financial Layer
                        </h4>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between py-0.5">
                            <span className="text-gray-500">
                              Base Reservation Rate
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(booking.bookingRevenue)}
                            </span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span className="text-gray-500">
                              Assessed Tax Volume
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(booking.taxAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span className="text-gray-500">
                              Cleared Transactions
                            </span>
                            <span className="font-medium text-slate-700">
                              {formatCurrency(booking.totalTransactionAmount)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col gap-2">
                        <label
                          htmlFor={`status-dropdown-${booking.id}`}
                          className="text-[11px] font-bold text-slate-500 uppercase tracking-wide"
                        >
                          Update Lifecycle Status
                        </label>
                        <select
                          id={`status-dropdown-${booking.id}`}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            onStatusUpdate(booking.bookingCode, e.target.value)
                          }
                          className="w-full text-xs font-semibold border border-slate-200 bg-slate-50 rounded-lg p-2 focus:ring-2 focus:ring-red-200 outline-none cursor-pointer"
                          defaultValue="Not contacted"
                        >
                          <option value="Not contacted">Not contacted</option>
                          <option value="Contacted">Contacted</option>
                          <option value="In progress">In progress</option>
                          <option value="Resolved">Resolved</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ==========================================
// 8. CORE CORE ORCHESTRATOR DASHBOARD CONTAINER
// ==========================================
const FleetRecoveryDashboard = () => {
  const cities = useSelector((state) => state?.cities?.citiesData);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const [getAllHubsListData, setGetAllHubsListData] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [apiError, setApiError] = useState(false);

  const [selectedSeverityFilter, setSelectedSeverityFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("highest-recovery");
  const [toast, setToast] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const [cityId, setCityId] = useState("1");
  const [hubId, setHubId] = useState("1");
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  const [visibleCount, setVisibleCount] = useState(10);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getAllHubsList = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?limit=1000`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  function formatDateForApi(date, type) {
    if (!date) return "";
    const pad = (n) => (n < 10 ? "0" + n : n);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    let hours = type === "start" ? "00" : "23";
    let minutes = type === "start" ? "00" : "59";
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const getRecoveryData = async () => {
    try {
      setLoading(true);
      setApiError(false);

      const formattedStartDate = formatDateForApi(selectionRange?.startDate);
      const formattedEndDate = formatDateForApi(selectionRange?.endDate);
      const queryParts = [];
      if (cityId) queryParts.push(`cityId=${cityId}`);
      if (hubId) queryParts.push(`hubId=${hubId}`);
      const queryString = queryParts.length ? `&${queryParts.join("&")}` : "";

      const response = await fetch(
        `${apiUrl}/api/dashboardreports/reconciled-recovery-ledger?filterType=bookingGenerateOn&startDate=${formattedStartDate}&endDate=${formattedEndDate}&${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();

      if (data?.statusCode === "0100") {
        const ledgerData = data?.dataValues?.reconciledBookingLedger || [];
        setAllBookings(ledgerData);
      } else {
        setAllBookings([]);
      }
    } catch (error) {
      console.error("Error fetching recovery ledger data:", error);
      setAllBookings([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRecoveryData();
    getAllHubsList();
  }, []);

  // Handler for booking row click - using query parameters like your example
  const handleBookingClick = (booking) => {
    // console.log("booking", booking)
    // Navigate using query parameters: viewallbookings?bookingid=BOOK123&display=true
    // This is similar to your example: viewallbookings?bookingid=-&display=true
    // window.location.href = `/viewallbookings?bookingid=${booking.bookingCode}&display=true`;
    const navigate = `/viewallbookings?bookingid=${booking?.bookingCode}&id=${booking?.id}&display=true`;
              window.open(navigate, "_blank");

    // If you want to use React Router with query parameters:
    // navigate(`/viewallbookings?bookingid=${booking.bookingCode}&display=true`);

    // If you want to pass the entire booking object as state:
    // navigate('/viewallbookings', {
    //   state: { booking },
    //   search: `?bookingid=${booking.bookingCode}&display=true`
    // });
  };

  const pendingRecoveryBookings = useMemo(() => {
    return allBookings.filter((b) => (b.recoveryAmount || 0) > 0);
  }, [allBookings]);

  const uniqueSources = useMemo(() => {
    return Array.from(
      new Set(pendingRecoveryBookings.map((b) => b.source).filter(Boolean)),
    );
  }, [pendingRecoveryBookings]);

  const uniqueStatuses = useMemo(() => {
    return Array.from(
      new Set(
        pendingRecoveryBookings.map((b) => b.bookingStatus).filter(Boolean),
      ),
    );
  }, [pendingRecoveryBookings]);

  const stats = useMemo(() => {
    const totalPendingAmount = pendingRecoveryBookings.reduce(
      (sum, b) => sum + (b.recoveryAmount || 0),
      0,
    );
    const totalSecurityDeposit = pendingRecoveryBookings.reduce(
      (sum, b) => sum + (b.securityDeposit || 0),
      0,
    );
    const totalCharges = pendingRecoveryBookings.reduce(
      (sum, b) => sum + (b.totalChargesAmount || 0),
      0,
    );
    const totalCollected = pendingRecoveryBookings.reduce(
      (sum, b) => sum + (b.totalTransactionAmount || 0),
      0,
    );

    const averagePending =
      pendingRecoveryBookings.length > 0
        ? totalPendingAmount / pendingRecoveryBookings.length
        : 0;
    const collectionRate =
      totalCharges > 0 ? (totalCollected / totalCharges) * 100 : 0;

    return {
      caseCount: pendingRecoveryBookings.length,
      totalPendingAmount,
      totalSecurityDeposit,
      totalCharges,
      averagePending,
      collectionRate,
    };
  }, [pendingRecoveryBookings]);

  const severityDistribution = useMemo(() => {
    const distribution = {
      Critical: {
        count: 0,
        amount: 0,
        color: "var(--color-severity-critical)",
      },
      High: { count: 0, amount: 0, color: "var(--color-severity-high)" },
      Medium: { count: 0, amount: 0, color: "var(--color-severity-medium)" },
      Low: { count: 0, amount: 0, color: "var(--color-severity-low)" },
    };
    pendingRecoveryBookings.forEach((b) => {
      const level = getSeverityLevel(b.recoveryAmount);
      if (distribution[level]) {
        distribution[level].count++;
        distribution[level].amount += b.recoveryAmount;
      }
    });
    return distribution;
  }, [pendingRecoveryBookings]);

  const sourceDistribution = useMemo(() => {
    const distribution = {};
    pendingRecoveryBookings.forEach((b) => {
      const source = b.source || "Unknown Hub";
      if (!distribution[source]) distribution[source] = { count: 0, amount: 0 };
      distribution[source].count++;
      distribution[source].amount += b.recoveryAmount;
    });
    return Object.entries(distribution)
      .map(([name, data], idx) => ({
        name,
        ...data,
        color: `hsl(${idx * 45}, 70%, 50%)`,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [pendingRecoveryBookings]);

  const monthlyTrend = useMemo(() => {
    const trends = {};
    pendingRecoveryBookings.forEach((b) => {
      const date = new Date(b.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!trends[monthKey]) {
        trends[monthKey] = {
          month: date.toLocaleString("default", { month: "short" }),
          amount: 0,
          count: 0,
          key: monthKey,
        };
      }
      trends[monthKey].amount += b.recoveryAmount;
      trends[monthKey].count++;
    });
    return Object.values(trends)
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(-6);
  }, [pendingRecoveryBookings]);

  const funnelData = useMemo(() => {
    const total = pendingRecoveryBookings.length;
    const critHigh =
      severityDistribution.Critical.count + severityDistribution.High.count;
    return [
      {
        stage: "Identified Exposure Cases",
        count: total,
        percentage: 100,
        color: "#1e293b",
      },
      {
        stage: "Critical / High Risk Tier",
        count: critHigh,
        percentage: total > 0 ? (critHigh / total) * 100 : 0,
        color: "var(--color-severity-critical)",
      },
      {
        stage: "Outreach Contacted",
        count: Math.floor(total * 0.42),
        percentage: 42,
        color: "#ea580c",
      },
      {
        stage: "Active Pipeline Progression",
        count: Math.floor(total * 0.22),
        percentage: 22,
        color: "#d97706",
      },
      {
        stage: "Reconciled Clearances",
        count: Math.floor(total * 0.08),
        percentage: 8,
        color: "#10b981",
      },
    ];
  }, [pendingRecoveryBookings, severityDistribution]);

  const topCases = useMemo(() => {
    return [...pendingRecoveryBookings]
      .sort((a, b) => b.recoveryAmount - a.recoveryAmount)
      .slice(0, 5);
  }, [pendingRecoveryBookings]);

  const filteredBookings = useMemo(() => {
    let result = [...pendingRecoveryBookings];
    if (statusFilter !== "all")
      result = result.filter((b) => b.bookingStatus === statusFilter);
    if (sourceFilter !== "all")
      result = result.filter((b) => b.source === sourceFilter);
    if (selectedSeverityFilter)
      result = result.filter(
        (b) => getSeverityLevel(b.recoveryAmount) === selectedSeverityFilter,
      );
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (b) =>
          (b.bookingCode || "").toLowerCase().includes(term) ||
          (b.source || "").toLowerCase().includes(term),
      );
    }
    if (sortBy === "highest-recovery")
      result.sort((a, b) => b.recoveryAmount - a.recoveryAmount);
    else if (sortBy === "newest")
      result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    else if (sortBy === "oldest")
      result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return result;
  }, [
    pendingRecoveryBookings,
    statusFilter,
    sourceFilter,
    selectedSeverityFilter,
    searchTerm,
    sortBy,
  ]);

  const handleExportCSV = () => {
    const escapeCSV = (val) => {
      if (val === undefined || val === null) return '""';
      const cleanStr = String(val).replace(/"/g, '""');
      return cleanStr.includes(",") ||
        cleanStr.includes('"') ||
        cleanStr.includes("\n")
        ? `"${cleanStr}"`
        : cleanStr;
    };

    const headers = [
      "Booking Code",
      "Booking Status",
      "Source",
      "Security Deposit",
      "Total Charges",
      "Net Shortfall Recovery Amount",
      "Severity Level",
      "Tenure Spectrum",
    ];
    const rows = filteredBookings.map((b) => [
      b.bookingCode || "",
      b.bookingStatus || "",
      b.source || "",
      formatCurrency(b.securityDeposit),
      formatCurrency(b.totalChargesAmount),
      formatCurrency(b.recoveryAmount),
      getRecoverySeverity(b.recoveryAmount).label,
      `${formatDateTime(b.bookFrom).split(",")[0]} to ${formatDateTime(b.bookTo).split(",")[0]}`,
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map(escapeCSV).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `Recovery_Ledger_Export_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SkeletonState = () => (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="md:col-span-5 h-36 bg-slate-200 rounded-2xl" />
        <div className="md:col-span-7 grid grid-cols-3 gap-4">
          <div className="bg-slate-200 rounded-xl h-36" />
          <div className="bg-slate-200 rounded-xl h-36" />
          <div className="bg-slate-200 rounded-xl h-36" />
        </div>
      </div>
      <div className="h-44 bg-slate-200 rounded-xl" />
      <div className="h-64 bg-slate-200 rounded-xl" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans text-slate-800 my-6">
      <style>{`
        :root {
          --color-severity-critical: #dc2626;
          --color-severity-high: #ea580c;
          --color-severity-medium: #d97706;
          --color-severity-low: #64748b;
        }
      `}</style>

      <div className="flex justify-between items-start md:items-center gap-2 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">
            Fleet Recovery Operations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor and manage pending recovery cases across the fleet.
          </p>
        </div>

        <div className="flex bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm gap-4 animate-in slide-in-from-top-4 duration-200">
          <DaterangePicker
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            style={-10}
          />
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
          >
            <option>Select City</option>
            {cities &&
              Object.entries(cities).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
          </select>
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={hubId}
            onChange={(e) => setHubId(e.target.value)}
          >
            <option>Select Hub</option>
            {getAllHubsListData &&
              getAllHubsListData.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.hubName}
                </option>
              ))}
          </select>

          <button
            onClick={() => {
              getRecoveryData();
            }}
            className="p-2 bg-blue-400"
          >
            Search
          </button>
          <button
            onClick={() => {
              setCityId("");
              setHubId("");
              const resetRange = {
                startDate: oneMonthAgo,
                endDate: today,
                key: "selection",
              };
              setSelectionRange(resetRange);
              getRecoveryData();
            }}
            className="p-2 bg-red-400"
          >
            Clear
          </button>
        </div>
      </div>

      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 flex items-center justify-between shadow-xs">
          <p className="text-red-700 text-xs font-medium flex items-center gap-2">
            <i className="ti ti-alert-triangle text-sm" /> Live ledger ledger
            synchronization delayed. Check local security tokens.
          </p>
          <button
            onClick={getRecoveryData}
            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold transition-all"
          >
            Retry Sync
          </button>
        </div>
      )}

      {loading ? (
        <SkeletonState />
      ) : (
        <>
          <StatisticsCards
            stats={stats}
            monthlyTrend={monthlyTrend}
            criticalCount={severityDistribution.Critical.count}
          />

          <ChartsRow
            severityDistribution={severityDistribution}
            sourceDistribution={sourceDistribution}
            monthlyTrend={monthlyTrend}
            selectedSeverity={selectedSeverityFilter}
            onSeverityClick={setSelectedSeverityFilter}
          />

          <RecoveryFunnel funnelData={funnelData} />

          <TopCasesWidget
            topCases={topCases}
            onInitiateCall={(code) =>
              showToast(
                `Outbound arbitration routing call active matching case: ${code}`,
                "success",
              )
            }
          />

          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            sourceFilter={sourceFilter}
            setSourceFilter={setSourceFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            uniqueStatuses={uniqueStatuses}
            uniqueSources={uniqueSources}
            handleExportCSV={handleExportCSV}
            disableExport={filteredBookings.length === 0}
          />

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            {filteredBookings.length === 0 ? (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
                  <i className="ti ti-check text-slate-400 text-xl" />
                </div>
                <h3 className="text-sm font-bold text-slate-700 mb-0.5">
                  Clear operational state
                </h3>
                <p className="text-xs text-gray-400">
                  No unearned liabilities match your active query segmentation
                  rules matrix parameters.
                </p>
              </div>
            ) : (
              <>
                <RecoveryTable
                  bookings={filteredBookings.slice(0, visibleCount)}
                  expandedRowId={expandedRowId}
                  toggleRowExpansion={(id) =>
                    setExpandedRowId(expandedRowId === id ? null : id)
                  }
                  handleRowKeyDown={(e, id) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpandedRowId(expandedRowId === id ? null : id);
                    }
                  }}
                  onStatusUpdate={(code, nextStatus) =>
                    showToast(
                      `Booking payload configuration (${code}) updated → [Stage: ${nextStatus}]`,
                      "success",
                    )
                  }
                  onBookingClick={handleBookingClick}
                />

                {filteredBookings.length > visibleCount && (
                  <div className="p-3 bg-slate-50/50 text-center border-t border-slate-100">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                      className="px-4 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-semibold border border-slate-200 rounded-lg text-xs shadow-xs transition-colors"
                    >
                      Load More Recovery Cases (
                      {filteredBookings.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="rounded-xl bg-slate-900 text-white font-medium shadow-xl px-4 py-3 flex items-center gap-2.5 text-xs border border-slate-800">
            <i className="ti ti-circle-check text-emerald-400 text-sm" />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetRecoveryDashboard;
